"use client";

import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { sessionActions } from "./store";

const cssColorToHex = (color) => {
  if (!color || typeof color !== "string") return null;
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.fillStyle = color;
    const computed = ctx.fillStyle;
    if (computed.startsWith("rgb")) {
      const rgb = computed.match(/\d+/g).map(Number);
      return `#${rgb.map((x) => x.toString(16).padStart(2, "0")).join("")}`;
    }
    return computed;
  } catch {
    return null;
  }
};

const RevendaProvider = ({ children }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;

    const fetchRevenda = async () => {
      try {
        const fullHost = window.location.host;
        const cleanHost = fullHost.replace("www.", "");
        const apiUrl = `/api/revendas/public?domain=${encodeURIComponent(cleanHost)}`;

        const response = await fetch(apiUrl, {
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) return;

        const revendas = await response.json();
        if (!revendas?.length) return;

        const currentRevenda = revendas[0];
        const themeBaseUrl = `/themes/${currentRevenda.code}`;

        // Carregar CSS apenas se existir
        try {
          const cssResponse = await fetch(`${themeBaseUrl}/styles.css`, { method: 'HEAD' });
          if (cssResponse.ok) {
            const link = document.createElement('link');
            link.href = `${themeBaseUrl}/styles.css`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
          }
        } catch (error) {
          console.warn('CSS do tema não encontrado:', error);
        }

        const loadIcons = async () => {
          const iconNames = [
            'pwa-64x64.png',
            'pwa-192x192.png',
            'pwa-512x512.png',
            'maskable-icon-512x512.png',
            'apple-touch-icon-180x180.png',
            'favicon.ico'
          ];

          for (const iconName of iconNames) {
            try {
              const iconResponse = await fetch(`${themeBaseUrl}/${iconName}`, { method: 'HEAD' });
              if (iconResponse.ok) {
                const existingLink = document.querySelector(`link[href*="${iconName}"]`);
                if (existingLink) {
                  existingLink.href = `${themeBaseUrl}/${iconName}`;
                } else {
                  const link = document.createElement('link');
                  if (iconName === 'favicon.ico') {
                    link.rel = 'icon';
                  } else if (iconName === 'apple-touch-icon-180x180.png') {
                    link.rel = 'apple-touch-icon';
                    link.sizes = '180x180';
                  } else {
                    link.rel = 'icon';
                    link.sizes = iconName.includes('64x64') ? '64x64' :
                                 iconName.includes('192x192') ? '192x192' : '512x512';
                  }
                  link.href = `${themeBaseUrl}/${iconName}`;
                  document.head.appendChild(link);
                }
              }
            } catch (error) {
              console.warn(`Ícone ${iconName} não encontrado:`, error);
            }
          }
        };

        // Atualizar manifest com cores reais
        const updateManifest = () => {
          const primaryColor = cssColorToHex(currentRevenda.color1) || '#1976d2';
          const backgroundColor = cssColorToHex(currentRevenda.background) || '#ffffff';

          const manifestLink = document.querySelector('link[rel="manifest"]');
          if (manifestLink) {
            fetch(manifestLink.href)
              .then(response => response.json())
              .then(manifest => {
                // Substituir variáveis template por valores reais
                const updatedManifest = JSON.stringify(manifest)
                  .replace(/\${colorPrimary}/g, primaryColor)
                  .replace(/\${colorBackground}/g, backgroundColor)
                  .replace(/\${themeName}/g, currentRevenda.title);

                const blob = new Blob([updatedManifest], { type: 'application/json' });
                const url = URL.createObjectURL(blob);

                const newManifestLink = document.createElement('link');
                newManifestLink.rel = 'manifest';
                newManifestLink.href = url;
                document.head.appendChild(newManifestLink);

                // Remover o antigo após um delay
                setTimeout(() => {
                  if (document.contains(manifestLink)) {
                    document.head.removeChild(manifestLink);
                  }
                }, 100);
              })
              .catch(error => {
                console.warn('Erro ao atualizar manifest:', error);
              });
          }
        };

        if (isMounted) {
          dispatch(sessionActions.setRevenda({
            ...currentRevenda,
            logo: `/api/revendas/logo/${currentRevenda.code}`,
            logoInverted: `/api/revendas/logo/${currentRevenda.code}?inverted=true`,
            colors: {
              primary: cssColorToHex(currentRevenda.color1) || '#1976d2',
              success: cssColorToHex(currentRevenda.color2) || '#4caf50',
              error: cssColorToHex(currentRevenda.color3) || '#f44336',
              white: cssColorToHex(currentRevenda.color4) || '#ffffff',
              neutral: cssColorToHex(currentRevenda.color5) || '#cccccc'
            },
            icons: {
              pwa64: `${themeBaseUrl}/pwa-64x64.png`,
              pwa192: `${themeBaseUrl}/pwa-192x192.png`,
              pwa512: `${themeBaseUrl}/pwa-512x512.png`,
              maskable: `${themeBaseUrl}/maskable-icon-512x512.png`,
              appleTouch: `${themeBaseUrl}/apple-touch-icon-180x180.png`,
              favicon: `${themeBaseUrl}/favicon.ico`
            },
            googleplay: currentRevenda.googleplay,
            applestore: currentRevenda.applestore
          }));

          document.title = currentRevenda.title;
          loadIcons();
          updateManifest();
        }

      } catch (error) {
        console.error('Erro ao carregar revenda:', error);
        document.title = "Sistema de Rastreamento";
      }
    };

    fetchRevenda();
    return () => { isMounted = false; };
  }, [dispatch]);

  return children;
};

export default RevendaProvider;